import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ModernButton } from '../../components/ui/ModernButton';
import { CountIndicator, InsightPriorityIndicator } from '../../components/ui/ModernIndicators';
import { TableErrorBoundary } from '../../components/ErrorBoundary';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Chart } from 'primereact/chart';
import { TabPanel, TabView } from 'primereact/tabview';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { useAuth } from '../../contexts/AuthContext';
import { InsightService, InsightStats } from '../../services/insightService';
import { CustomerService } from '../../services/customerService';
import { ActivityService } from '../../services/activityService';
import { AgentInsight, InsightPriority, InsightStatus, InsightType } from '../../types/agent';
import { LeadBrief } from '../../components/LeadBrief';
import './InsightsPage.css';

interface InsightFilters {
	status: InsightStatus | 'all';
	priority: InsightPriority | 'all';
	type: InsightType | 'all';
	dateRange: Date[];
}

export const InsightsPage: React.FC = () => {
	const { currentAgent } = useAuth();
	const toast = useRef<Toast>( null );

	const [ activeTab, setActiveTab ] = useState( 0 );
	const [ insights, setInsights ] = useState<AgentInsight[]>( [] );
	const [ loading, setLoading ] = useState( true );
	const [ stats, setStats ] = useState<InsightStats | null>( null );
	const [ selectedInsight, setSelectedInsight ] = useState<AgentInsight | null>( null );
	const [ showDetailDialog, setShowDetailDialog ] = useState( false );
	const [ showActionDialog, setShowActionDialog ] = useState( false );
	const [ actionNotes, setActionNotes ] = useState( '' );
	const [ trendingInsights, setTrendingInsights ] = useState<AgentInsight[]>( [] );

	// Edit dialog state
	const [ editStatus, setEditStatus ] = useState<InsightStatus>( 'new' );
	const [ editPriority, setEditPriority ] = useState<InsightPriority>( 'medium' );
	const [ editLeadScore, setEditLeadScore ] = useState<number>( 0 );
	const [ isSaving, setIsSaving ] = useState( false );

	const [ filters, setFilters ] = useState<InsightFilters>( {
		status: 'all', priority: 'all', type: 'all', dateRange: [ new Date( new Date().setDate( new Date().getDate() - 30 ) ), new Date() ]
	} );

	const statusOptions = [ { label: 'All Status', value: 'all' }, { label: 'New', value: 'new' }, { label: 'Viewed', value: 'viewed' }, { label: 'Contacted', value: 'contacted' }, { label: 'In Progress', value: 'in_progress' }, { label: 'Converted', value: 'converted' }, { label: 'Dismissed', value: 'dismissed' } ];

	const priorityOptions = [ { label: 'All Priorities', value: 'all' }, { label: 'Urgent', value: 'urgent' }, { label: 'High', value: 'high' }, { label: 'Medium', value: 'medium' }, { label: 'Low', value: 'low' } ];

	const typeOptions = [ { label: 'All Types', value: 'all' }, { label: 'Refinance Opportunity', value: 'refinance_opportunity' }, { label: 'Rate Alert', value: 'rate_alert' }, { label: 'Payment Stress', value: 'payment_stress' }, { label: 'Calculator Engagement', value: 'calculator_engagement' }, {
		label: 'First Time Buyer', value: 'first_time_buyer'
	}, { label: 'High Value Prospect', value: 'high_value_prospect' }, { label: 'Pre-Approval Ready', value: 'pre_approval_ready' }, { label: 'Inactive User', value: 'inactive_user' },
		{ label: 'Debt Consolidation', value: 'debt_consolidation' }, { label: 'Home Equity', value: 'home_equity_opportunity' },
		{ label: 'Market Timing', value: 'market_timing' }, { label: 'Financial Milestone', value: 'financial_milestone' },
		{ label: 'Agent Gift', value: 'agent_gift' } ];

	const loadInsights = useCallback( async () => {
		setLoading( true );
		try {
			const filterParams: {
				status?: InsightStatus; priority?: InsightPriority; type?: InsightType; assignedAgentId?: string; dateFrom?: Date; dateTo?: Date;
			} = {};

			if ( filters.status !== 'all' ) filterParams.status = filters.status;
			if ( filters.priority !== 'all' ) filterParams.priority = filters.priority;
			if ( filters.type !== 'all' ) filterParams.type = filters.type;
			if ( filters.dateRange && filters.dateRange[ 0 ] && filters.dateRange[ 1 ] ) {
				filterParams.dateFrom = filters.dateRange[ 0 ];
				filterParams.dateTo = filters.dateRange[ 1 ];
			}

			let data: AgentInsight[];
			if ( currentAgent?.role === 'master_agent' || currentAgent?.role === 'super_agent' ) {
				data = await InsightService.getAllInsights( filterParams );
			} else {
				data = await InsightService.getAgentInsights( currentAgent?.id || '', filterParams );
			}
			setInsights( data );
		} catch ( error ) {
			console.error( 'Error loading insights:', error );
			toast.current?.show( {
				severity: 'error', summary: 'Error', detail: 'Failed to load insights', life: 3000
			} );
		} finally {
			setLoading( false );
		}
	}, [ currentAgent, filters ] );

	const loadStats = useCallback( async () => {
		try {
			const agentId = (currentAgent?.role === 'master_agent' || currentAgent?.role === 'super_agent') ? undefined : currentAgent?.id;
			const statsData = await InsightService.getInsightStats( agentId );
			setStats( statsData );
		} catch ( error ) {
			console.error( 'Error loading stats:', error );
			toast.current?.show( {
				severity: 'error', summary: 'Error', detail: 'Failed to load stats', life: 3000
			} );
		}
	}, [ currentAgent ] );

	const loadTrendingInsights = useCallback( async () => {
		try {
			const agentId = (currentAgent?.role === 'master_agent' || currentAgent?.role === 'super_agent') ? undefined : currentAgent?.id;
			const trends = await InsightService.getTrendingInsights( agentId, 7 );
			// Transform trends to match expected format
			const formattedTrends = trends.map( ( trend ) => ( {
				type: trend.type, count: trend.count, trend: trend.trend
			} ) );
			setTrendingInsights( formattedTrends as unknown as AgentInsight[] );
		} catch ( error ) {
			console.error( 'Error loading trending insights:', error );
			// Silently fail trending insights loading - not critical
		}
	}, [ currentAgent ] );

	useEffect( () => {
		const loadData = async () => {
			await Promise.all([
				loadInsights(),
				loadStats(),
				loadTrendingInsights()
			]);
		};

		loadData();
	}, [ loadInsights, loadStats, loadTrendingInsights ] );

	const handleViewInsight = async( insight: AgentInsight ) => {
		setSelectedInsight( insight );
		setEditStatus( insight.status );
		setEditPriority( insight.priority );
		setEditLeadScore( insight.leadScore );
		setShowDetailDialog( true );

		if ( !insight.viewed ) {
			try {
				await InsightService.markInsightAsViewed( insight.id );
				await Promise.all([
					loadInsights(),
					loadStats()
				]);
			} catch ( error ) {
				console.error( 'Error marking insight as viewed:', error );
				// Not critical - continue showing the dialog
			}
		}

		// Log activity
		if (currentAgent?.id) {
			await ActivityService.logActivity(
				currentAgent.id,
				'insight_viewed',
				'insight',
				insight.id,
				insight.title,
				{
					insightPriority: insight.priority,
					insightType: insight.type,
					customerEmail: insight.customerInfo?.email
				}
			);
		}
	};

	const handleTakeAction = ( insight: AgentInsight ) => {
		setSelectedInsight( insight );
		setActionNotes( '' );
		setShowActionDialog( true );
	};

	const handleSaveInsight = async() => {
		if ( !selectedInsight ) return;

		setIsSaving( true );
		try {
			await InsightService.updateInsight( selectedInsight.id, {
				status: editStatus,
				priority: editPriority,
				leadScore: editLeadScore
			} );

			await Promise.all( [
				loadInsights(),
				loadStats()
			] );

			// Log activity
			if ( currentAgent?.id ) {
				await ActivityService.logActivity(
					currentAgent.id,
					'insight_status_changed',
					'insight',
					selectedInsight.id,
					selectedInsight.title,
					{
						oldStatus: selectedInsight.status,
						newStatus: editStatus,
						insightPriority: editPriority
					}
				);
			}

			setShowDetailDialog( false );
			toast.current?.show( {
				severity: 'success', summary: 'Success', detail: 'Insight updated successfully', life: 3000
			} );
		} catch ( error ) {
			console.error( 'Error updating insight:', error );
			toast.current?.show( {
				severity: 'error', summary: 'Error', detail: 'Failed to update insight', life: 3000
			} );
		} finally {
			setIsSaving( false );
		}
	};

	const handleSaveAction = async() => {
		if ( !selectedInsight ) return;

		const notes = actionNotes.trim();

		try {
			// Update insight status to contacted
			await InsightService.updateInsightStatus( selectedInsight.id, 'contacted' );

			// Record interaction if customer exists
			if ( selectedInsight.customerId ) {
				await CustomerService.recordInteraction( selectedInsight.customerId, 'note' );
			}

			// Log activity - this is what persists the action notes
			if ( currentAgent?.id ) {
				await ActivityService.logActivity(
					currentAgent.id,
					'insight_status_changed',
					'insight',
					selectedInsight.id,
					selectedInsight.title,
					{
						oldStatus: selectedInsight.status,
						newStatus: 'contacted',
						insightPriority: selectedInsight.priority,
						insightType: selectedInsight.type,
						actionNotes: notes
					}
				);
			}

			await Promise.all([
				loadInsights(),
				loadStats()
			]);

			setShowActionDialog( false );

			// Without an agent id there is nowhere to store the notes - say so instead of claiming success
			if ( notes && !currentAgent?.id ) {
				toast.current?.show( {
					severity: 'warn', summary: 'Partially Saved', detail: 'Action recorded, but the notes could not be saved', life: 5000
				} );
			} else {
				toast.current?.show( {
					severity: 'success', summary: 'Success', detail: 'Action recorded successfully', life: 3000
				} );
			}
		} catch ( error ) {
			console.error( 'Error saving action:', error );
			toast.current?.show( {
				severity: 'error', summary: 'Error', detail: 'Failed to save action', life: 3000
			} );
		}
	};

	// Column templates
	const priorityBodyTemplate = ( rowData: AgentInsight ) => {
		return <InsightPriorityIndicator priority={ rowData.priority }/>;
	};

	const typeBodyTemplate = ( rowData: AgentInsight ) => {
		const typeLabels: Record<InsightType, string> = {
			'refinance_opportunity': 'Refinance', 'rate_alert': 'Rate Alert', 'payment_stress': 'Payment Stress', 'calculator_engagement': 'Calculator Use', 'first_time_buyer': 'First Time Buyer', 'high_value_prospect': 'High Value', 'pre_approval_ready': 'Pre-Approval', 'inactive_user': 'Inactive', 'agent_gift': 'Agent Gift',
			'debt_consolidation': 'Debt Consolidation', 'home_equity_opportunity': 'Home Equity',
			'market_timing': 'Market Timing', 'financial_milestone': 'Milestone'
		};

		const typeLabel = typeLabels[ rowData.type ] || String( rowData.type || 'unknown' ).replace( /_/g, ' ' );

		return ( <div className="insight-type">
			<i className={ `pi pi-${ getInsightIcon( rowData.type ) }` }></i>
			<span>{ typeLabel }</span>
		</div> );
	};

	const leadScoreBodyTemplate = ( rowData: AgentInsight ) => {
		return ( <div className="lead-score-cell">
			<span className="score">{ rowData.leadScore }</span>
			<ProgressBar
				value={ rowData.leadScore }
				showValue={ false }
				style={ { height: '6px', width: '60px' } }
			/>
		</div> );
	};

	const statusBodyTemplate = ( rowData: AgentInsight ) => {
		const statusConfig: Record<InsightStatus, { icon: string; color: string }> = {
			'new': { icon: 'circle', color: '#3498db' },
			'viewed': { icon: 'eye', color: '#95a5a6' },
			'contacted': { icon: 'phone', color: '#f39c12' },
			'in_progress': { icon: 'clock', color: '#9b59b6' },
			'converted': { icon: 'check-circle', color: '#2ecc71' },
			'resolved': { icon: 'check', color: '#27ae60' },
			'dismissed': { icon: 'times-circle', color: '#7f8c8d' },
			'archived': { icon: 'archive', color: '#bdc3c7' }
		};

		const config = statusConfig[ rowData.status ] || { icon: 'circle', color: '#95a5a6' };
		const statusLabel = String( rowData.status || 'unknown' ).replace( /_/g, ' ' );

		return ( <div className="status-cell" style={ { color: config.color } }>
			<i className={ `pi pi-${ config.icon }` }></i>
			<span>{ statusLabel }</span>
		</div> );
	};

	const actionsBodyTemplate = ( rowData: AgentInsight ) => {
		return ( <div className="action-buttons">
			<ModernButton
				variant="ghost"
				size="sm"
				icon="pi pi-eye"
				onClick={ () => handleViewInsight( rowData ) }
				title="View insight"
			/>
			<ModernButton
				variant="ghost"
				size="sm"
				icon="pi pi-phone"
				onClick={ () => handleTakeAction( rowData ) }
				disabled={ rowData.status === 'converted' || rowData.status === 'dismissed' }
				title="Take action"
			/>
			<ModernButton
				variant="ghost"
				size="sm"
				icon="pi pi-pencil"
				onClick={ () => handleViewInsight( rowData ) }
				title="Update status"
			/>
		</div> );
	};

	const getInsightIcon = ( type: InsightType ): string => {
		const iconMap: Record<InsightType, string> = {
			'refinance_opportunity': 'dollar', 'agent_gift': 'circle-dollar-to-slot', 'rate_alert': 'chart-line', 'payment_stress': 'exclamation-triangle', 'calculator_engagement': 'calculator', 'first_time_buyer': 'home', 'high_value_prospect': 'star', 'pre_approval_ready': 'check-square', 'inactive_user': 'user-minus',
			'debt_consolidation': 'credit-card', 'home_equity_opportunity': 'house-chimney',
			'market_timing': 'arrow-trend-down', 'financial_milestone': 'flag-checkered'
		};
		return iconMap[ type ] || 'info-circle';
	};

	// Charts configuration
	const priorityChartData = stats ? {
		labels: [ 'Urgent', 'High', 'Medium', 'Low' ], datasets: [ {
			data: [ stats.insightsByPriority[ 'urgent' ] || 0, stats.insightsByPriority[ 'high' ] || 0, stats.insightsByPriority[ 'medium' ] || 0, stats.insightsByPriority[ 'low' ] || 0 ], backgroundColor: [ '#e74c3c', '#f39c12', '#3498db', '#95a5a6' ]
		} ]
	} : null;

	const typeChartData = stats ? {
		labels: Object.keys( stats.insightsByType ).map( type => type.replace( /_/g, ' ' ) ), datasets: [ {
			label: 'Insights by Type', data: Object.values( stats.insightsByType ), backgroundColor: '#3498db', borderColor: '#2980b9', borderWidth: 1
		} ]
	} : null;

	const chartOptions = {
		responsive: true, maintainAspectRatio: false, plugins: {
			legend: {
				display: false
			}
		}
	};

	return ( <div className="insights-page">
		<Toast ref={ toast }/>

		<div className="page-header">
			<h1>Customer Insights</h1>
			<p>AI-powered insights to help you engage with customers at the right time</p>
		</div>

		{/* Stats Overview */ }
		{ stats && ( <div className="stats-grid">
			<Card className="stat-card">
				<div className="stat-content">
					<div className="stat-icon total-icon">
						<i className="pi pi-chart-bar"></i>
					</div>
					<div className="stat-details">
						<h3>{ stats.totalInsights }</h3>
						<p>Total Insights</p>
					</div>
				</div>
			</Card>

			<Card className="stat-card">
				<div className="stat-content">
					<div className="stat-icon new-icon">
						<i className="pi pi-sparkles"></i>
					</div>
					<div className="stat-details">
						<h3>{ stats.newInsights }</h3>
						<p>New Insights</p>
					</div>
				</div>
			</Card>

			<Card className="stat-card">
				<div className="stat-content">
					<div className="stat-icon urgent-icon">
						<i className="pi pi-exclamation-triangle"></i>
					</div>
					<div className="stat-details">
						<h3>{ stats.urgentInsights }</h3>
						<p>Urgent Actions</p>
					</div>
				</div>
			</Card>

			<Card className="stat-card">
				<div className="stat-content">
					<div className="stat-icon conversion-icon">
						<i className="pi pi-percentage"></i>
					</div>
					<div className="stat-details">
						<h3>{ stats.conversionRate.toFixed( 1 ) }%</h3>
						<p>Conversion Rate</p>
					</div>
				</div>
			</Card>
		</div> ) }

		<TabView activeIndex={ activeTab } onTabChange={ ( e ) => setActiveTab( e.index ) }>
			<TabPanel header="All Insights">
				{/* Filters */ }
				<Card className="filters-card">
					<div className="filters-container">
						<div className="filter-item">
							<label>Status</label>
							<Dropdown
								value={ filters.status }
								options={ statusOptions }
								onChange={ ( e ) => setFilters( { ...filters, status: e.value } ) }
								placeholder="All Status"
							/>
						</div>

						<div className="filter-item">
							<label>Priority</label>
							<Dropdown
								value={ filters.priority }
								options={ priorityOptions }
								onChange={ ( e ) => setFilters( { ...filters, priority: e.value } ) }
								placeholder="All Priorities"
							/>
						</div>

						<div className="filter-item">
							<label>Type</label>
							<Dropdown
								value={ filters.type }
								options={ typeOptions }
								onChange={ ( e ) => setFilters( { ...filters, type: e.value } ) }
								placeholder="All Types"
							/>
						</div>

						<div className="filter-item">
							<label>Date Range</label>
							<Calendar
								value={ filters.dateRange }
								onChange={ ( e ) => setFilters( { ...filters, dateRange: e.value as Date[] } ) }
								selectionMode="range"
								readOnlyInput
								showIcon
							/>
						</div>
					</div>
				</Card>

				{/* Insights Table */ }
				<Card className="insights-table-card">
					<TableErrorBoundary>
						<DataTable
							value={ insights }
							loading={ loading }
							paginator
							rows={ 10 }
							rowsPerPageOptions={ [ 10, 25, 50 ] }
							emptyMessage="No insights found."
							className="insights-table"
						>
							<Column field="priority" header="Priority" body={ priorityBodyTemplate } style={ { width: '100px' } }/>
							<Column field="type" header="Type" body={ typeBodyTemplate } style={ { width: '180px' } }/>
							<Column field="title" header="Insight"/>
							<Column field="leadScore" header="Score" body={ leadScoreBodyTemplate } style={ { width: '120px' } } sortable/>
							<Column field="status" header="Status" body={ statusBodyTemplate } style={ { width: '150px' } }/>
							<Column field="createdAt" header="Created" body={ ( rowData ) => new Date( rowData.createdAt ).toLocaleDateString() } style={ { width: '100px' } } sortable/>
							<Column header="Actions" body={ actionsBodyTemplate } headerStyle={ { width: '10rem', textAlign: 'center' } } bodyStyle={ { textAlign: 'center' } }/>
						</DataTable>
					</TableErrorBoundary>
				</Card>
			</TabPanel>

			<TabPanel header="Analytics">
				<div className="analytics-grid">
					<Card className="chart-card">
						<h3>Insights by Priority</h3>
						{ priorityChartData && ( <Chart type="doughnut" data={ priorityChartData } options={ chartOptions } style={ { height: '300px' } }/> ) }
					</Card>

					<Card className="chart-card">
						<h3>Insights by Type</h3>
						{ typeChartData && ( <Chart type="bar" data={ typeChartData } options={ chartOptions } style={ { height: '300px' } }/> ) }
					</Card>

					<Card className="trending-card">
						<h3>Trending Insights (Last 7 Days)</h3>
						<div className="trending-list">
							{ trendingInsights.map( ( trend, index ) => ( <div key={ index } className="trending-item">
								<div className="trend-info">
									<i className={ `pi pi-${ getInsightIcon( trend.type ) }` }></i>
									<span>{ String( trend.type || 'unknown' ).replace( /_/g, ' ' ) }</span>
								</div>
								<div className="trend-count">
									<CountIndicator count={ trend.count ?? 0 }/>
									{ trend.trend === 'up' && <i className="pi pi-arrow-up trend-up"></i> }
									{ trend.trend === 'down' && <i className="pi pi-arrow-down trend-down"></i> }
								</div>
							</div> ) ) }
						</div>
					</Card>
				</div>
			</TabPanel>
		</TabView>

		{/* Insight Detail Dialog */ }
		<Dialog
			visible={ showDetailDialog }
			onHide={ () => setShowDetailDialog( false ) }
			header={ selectedInsight?.title }
			style={ { width: '50vw' } }
			breakpoints={ { '960px': '75vw', '640px': '90vw' } }
			footer={ <div className="modern-form-actions">
				<ModernButton variant="ghost" onClick={ () => setShowDetailDialog( false ) }>
					Cancel
				</ModernButton>
				<ModernButton variant="primary" onClick={ handleSaveInsight } disabled={ isSaving }>
					{ isSaving ? 'Saving...' : 'Save Changes' }
				</ModernButton>
			</div> }
		>
			{ selectedInsight && ( <div className="insight-detail">
				<div className="detail-section">
					<h4>Description</h4>
					<p>{ selectedInsight.description }</p>
				</div>

				<div className="detail-section">
					<h4>Recommended Action</h4>
					<p>{ selectedInsight.contactSuggestion }</p>
				</div>

				<div className="detail-grid">
					<div className="detail-item">
						<label>Priority</label>
						<Dropdown
							value={ editPriority }
							options={ priorityOptions.slice( 1 ) }
							onChange={ ( e ) => setEditPriority( e.value ) }
							className="edit-dropdown"
						/>
					</div>

					<div className="detail-item">
						<label>Lead Score</label>
						<InputNumber
							value={ editLeadScore }
							onValueChange={ ( e ) => setEditLeadScore( e.value || 0 ) }
							min={ 0 }
							max={ 100 }
							showButtons
							className="edit-lead-score"
						/>
					</div>

					<div className="detail-item">
						<label>Status</label>
						<Dropdown
							value={ editStatus }
							options={ statusOptions.slice( 1 ) }
							onChange={ ( e ) => setEditStatus( e.value ) }
							className="edit-dropdown"
						/>
					</div>

					<div className="detail-item">
						<label>Expires</label>
						<span>{ new Date( selectedInsight.expiresAt ).toLocaleDateString() }</span>
					</div>
				</div>

				{/*
					The backend attaches a trigger, qualification numbers, action items
					and contact preferences to every insight, and none of it was ever
					read. LeadBrief renders whichever of those a given insight has.
				*/}
				<LeadBrief insight={ selectedInsight } />

				{ selectedInsight.data && Object.keys( selectedInsight.data ).length > 0 && ( <div className="detail-section">
					<h4>Additional Data</h4>
					<div className="data-grid">
						{ Object.entries( selectedInsight.data ).map( ( [ key, value ] ) => ( <div key={ key } className="data-item">
							<label>{ key.replace( /([A-Z])/g, ' $1' ).trim() }</label>
							<span>{ String( value ) }</span>
						</div> ) ) }
					</div>
				</div> ) }
			</div> ) }
		</Dialog>

		{/* Action Dialog */ }
		<Dialog
			visible={ showActionDialog }
			onHide={ () => setShowActionDialog( false ) }
			header="Record Action"
			style={ { width: '40vw' } }
			breakpoints={ { '960px': '60vw', '640px': '90vw' } }
			footer={ <div className="modern-form-actions">
				<ModernButton variant="ghost" onClick={ () => setShowActionDialog( false ) }>
					Cancel
				</ModernButton>
				<ModernButton variant="primary" onClick={ handleSaveAction }>
					Save
				</ModernButton>
			</div> }
		>
			<div className="action-form">
				<p>Record your action for: <strong>{ selectedInsight?.title }</strong></p>

				<div className="field">
					<label htmlFor="actionNotes">Action Notes</label>
					<InputTextarea
						id="actionNotes"
						value={ actionNotes }
						onChange={ ( e ) => setActionNotes( e.target.value ) }
						rows={ 5 }
						placeholder="Describe the action taken..."
						style={ { width: '100%' } }
					/>
				</div>
			</div>
		</Dialog>
	</div> );
};

export default InsightsPage;
